import React, { useState, useEffect, useCallback } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    Typography,
    Divider,
    IconButton,
    InputAdornment,
    MenuItem,
} from '@mui/material';
import { Mic as MicIcon, MicOff as MicOffIcon } from '@mui/icons-material';
import useDictation from '../../../hooks/useDictation';
import AttendDaysPicker from './AttendDaysPicker';
import { attendOptions, nextAttendCode, splitCodes, NIGHT_CODE } from '../../../services/attendCodes';

// Полное значение кода уходит в payroll (PreCloseTimeiPad.AbsentCode), короткое — бейдж в ячейке.

export default function CellEditModal({
    open,
    onClose,
    employeeName,
    jobName,
    hours: initialHours,
    notes: initialNotes,
    attendCode: initialAttendCode,
    weekSummary,
    weekDates,
    currentDate,
    weekAttend,
    onSave,
}) {
    const [hours, setHours] = useState(0);
    const [notes, setNotes] = useState('');
    const [attendCode, setAttendCode] = useState('');
    // Дни недели, к которым применить код (мультивыбор). Текущий день — всегда в наборе.
    const [applyDates, setApplyDates] = useState([]);

    // Dictation
    const { isSupported: dictationSupported, isListening, start: startDictation, stop: stopDictation } = useDictation({
        lang: 'en-US', interimResults: false, continuous: false, safetyTimeoutMs: 15000,
    });
    const toggleDictation = useCallback(async () => {
        if (!dictationSupported) return;
        if (isListening) {
            stopDictation();
            return;
        }
        await startDictation({
            onResult: (transcript) => {
                if (!transcript) return;
                setNotes(prev => ((prev || '') + (prev ? ' ' : '') + transcript).trim());
            },
        });
    }, [dictationSupported, isListening, startDictation, stopDictation]);

    // Дни недели, где у этого сотрудника на этой работе уже стоит код `code`.
    const daysWithCode = useCallback((code) => (
        code ? Object.keys(weekAttend || {}).filter(d => weekAttend[d] === code) : []
    ), [weekAttend]);

    useEffect(() => {
        if (open) {
            setHours(initialHours || 0);
            setNotes(initialNotes || '');
            const code = initialAttendCode || '';
            setAttendCode(code);
            // Отмечены ровно те дни, где этот код УЖЕ стоит. Текущий день сюда
            // попадает сам, если код на нём есть; насильно его не добавляем —
            // иначе пустая ячейка открывалась с горящим чипом «сегодня».
            setApplyDates([...new Set(daysWithCode(code))].sort());
        }
    }, [open, initialHours, initialNotes, initialAttendCode, currentDate, daysWithCode]);

    // Смена кода: НЕ теряем то, что форман уже отметил руками — просто добавляем дни,
    // где новый код уже стоит (можно отметить дни и до выбора кода). Сброс в None
    // снимает только текущий день: остальные отмеченные дни живут своей жизнью.
    const handleCodeChange = (code) => {
        setAttendCode(code);
        setApplyDates(prev => {
            const next = new Set(prev);
            if (code) {
                daysWithCode(code).forEach(d => next.add(d));
                if (currentDate) next.add(currentDate);
            } else {
                next.delete(currentDate);
            }
            return [...next].sort();
        });
    };

    // Чип текущего дня и селект кода — ОДИН факт: сняли чип «сегодня» → кода на
    // этом дне нет. Без этого текущий день нельзя было отменить вообще.
    const handleDaysChange = (dates) => {
        setApplyDates(dates);
        if (attendCode && currentDate && !dates.includes(currentDate)) setAttendCode('');
    };

    const handleSave = () => {
        // Семантика пикера: отмеченные дни = где стоит код. Снятая галочка на дне,
        // где код был, означает «снять код с этого дня».
        //
        // Код для ОСТАЛЬНЫХ дней берём отдельно от текущего: сняв код с текущего
        // дня (attendCode = ''), форман не просил стереть его с Пн и Вт — а раньше
        // именно это и происходило, пустой код уезжал на всю отмеченную тройку.
        const dayCode = attendCode || initialAttendCode || '';
        const apply = dayCode ? applyDates.filter(d => d !== currentDate) : [];
        const remove = daysWithCode(initialAttendCode || '')
            .filter(d => d !== currentDate && !applyDates.includes(d));
        const dayOps = (apply.length || remove.length) ? { code: dayCode, apply, remove } : null;
        onSave(parseFloat(hours) || 0, notes, attendCode, dayOps);
        onClose();
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
            <DialogTitle sx={{ pb: 1 }}>
                Edit Hours
                <Typography variant="body2" color="text.secondary">
                    {employeeName} — {jobName}
                </Typography>
            </DialogTitle>
            <DialogContent>
                <TextField
                    label="Hours"
                    type="number"
                    value={hours}
                    onChange={(e) => {
                        const val = parseFloat(e.target.value);
                        if (e.target.value === '' || (val >= 0 && val <= 24)) {
                            setHours(e.target.value);
                        }
                    }}
                    inputProps={{ min: 0, max: 24, step: 0.5 }}
                    fullWidth
                    size="small"
                    sx={{ mb: 2, mt: 1 }}
                />

                {/* Чужие часы этого сотрудника СЕГОДНЯ (другой форман) — из локальной
                    базы, без запроса к серверу. Форман видит, сколько уже есть, чтобы
                    не превысить 24ч (сервер всё равно заблокирует переработку). */}
                {weekSummary?.otherHours?.length > 0 && (
                    <Box sx={{ mb: 2, p: 1, borderRadius: 1, bgcolor: '#fff7ed', border: '1px solid #fed7aa' }}>
                        <Typography variant="caption" sx={{ display: 'block', color: '#9a3412', fontWeight: 700 }}>
                            ⚠ Already booked today by another foreman:
                        </Typography>
                        {weekSummary.otherHours.map((o, i) => (
                            <Typography key={i} variant="body2" sx={{ color: '#9a3412', lineHeight: 1.4 }}>
                                {o.hours}h — {o.name}
                            </Typography>
                        ))}
                        <Typography variant="caption" sx={{ display: 'block', color: '#9a3412', mt: 0.5 }}>
                            Your hours add on top (24h/day max per worker).
                        </Typography>
                    </Box>
                )}

                <TextField
                    label="Notes"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    multiline
                    rows={3}
                    fullWidth
                    size="small"
                    sx={{ mb: 2 }}
                    InputProps={{
                        endAdornment: dictationSupported && (
                            <InputAdornment position="end" sx={{ alignSelf: 'flex-start', mt: 1 }}>
                                <IconButton
                                    onClick={toggleDictation}
                                    color={isListening ? 'error' : 'default'}
                                    size="small"
                                    title={isListening ? 'Stop recording' : 'Start voice input'}
                                >
                                    {isListening ? <MicOffIcon /> : <MicIcon />}
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                />

                {/* Absence code — отметка отсутствия этого сотрудника в этот день.
                    Обычный select: понятнее и компактнее, чем toggle-ряд. День с кодом
                    валиден с 0 часов (no-show). */}
                <TextField
                    select
                    label="Attendance code"
                    value={splitCodes(attendCode)}
                    onChange={(e) => handleCodeChange(nextAttendCode(attendCode, e.target.value))}
                    SelectProps={{ multiple: true, renderValue: (v) => v.join(', ') }}
                    fullWidth
                    size="small"
                    sx={{ mb: 2 }}
                    helperText={attendCode === NIGHT_CODE
                        ? 'Night shift for THIS worker. Days with no hours yet get the code once you enter hours.'
                        : (attendCode
                            ? 'Absence day — you can leave hours at 0. Several codes are allowed.'
                            : 'Absence codes, or Night work for a night-shift worker')}
                    FormHelperTextProps={{ sx: { color: attendCode === NIGHT_CODE ? '#4338ca' : (attendCode ? '#b91c1c' : undefined) } }}
                >
                    <MenuItem value="">
                        <em>None</em>
                    </MenuItem>
                    {attendOptions(attendCode).map(c => (
                        <MenuItem key={c.value} value={c.value}>
                            <Box component="span" sx={{
                                display: 'inline-block', minWidth: 26, mr: 1, px: 0.5,
                                borderRadius: '4px', bgcolor: c.night ? '#312e81' : '#dc2626', color: '#fff',
                                fontSize: '0.65rem', fontWeight: 700, textAlign: 'center', lineHeight: '16px',
                            }}>{c.short}</Box>
                            {c.night ? '🌙 ' : ''}{c.value}
                        </MenuItem>
                    ))}
                </TextField>

                {/* Мультивыбор дней: код ставится сразу на несколько дней недели
                    (ночная смена Пн–Ср, отсутствие два дня подряд). Отмеченные дни =
                    где код стоит; снятая галочка снимает код с того дня. */}
                {weekDates?.length > 0 && (
                    <AttendDaysPicker
                        weekDates={weekDates}
                        value={applyDates}
                        onChange={handleDaysChange}
                        night={attendCode === NIGHT_CODE}
                        currentDate={currentDate}
                        markedDates={Object.keys(weekAttend || {})}
                    />
                )}

                {weekSummary && (
                    <>
                        <Divider sx={{ my: 1 }} />
                        <Box sx={{ display: 'flex', gap: 3, mt: 1 }}>
                            <Box>
                                <Typography variant="caption" color="text.secondary">Day Total</Typography>
                                <Typography variant="body2" fontWeight={600} color="warning.dark">
                                    {weekSummary.dayTotal}h
                                </Typography>
                            </Box>
                            <Box>
                                <Typography variant="caption" color="text.secondary">Week Total</Typography>
                                <Typography variant="body2" fontWeight={600} color="success.dark">
                                    {weekSummary.weekTotal}h
                                </Typography>
                            </Box>
                            <Box>
                                <Typography variant="caption" color="text.secondary">Days Worked</Typography>
                                <Typography variant="body2" fontWeight={600}>
                                    {weekSummary.daysWorked}
                                </Typography>
                            </Box>
                        </Box>
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button onClick={handleSave} variant="contained">Save</Button>
            </DialogActions>
        </Dialog>
    );
}
